import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { apiFetch } from '../api';

const BASE_FILTERS = [
  { id: 'all', label: 'All Focus' },
  { id: 'foundations', label: 'Foundations' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'assessment', label: 'Assessments' },
  { id: 'confidence', label: 'Confidence' },
  { id: 'problem-solving', label: 'Problem Solving' },
];

const toTitle = (value) => value.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const SessionHistoryPanel = ({ onSessionSelect, selectedSessionId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [focusFilter, setFocusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(handle);
  }, [query]);

  const derivedFilters = useMemo(() => {
    const extras = (availableTags || [])
      .filter(tag => BASE_FILTERS.findIndex(f => f.id === tag) === -1)
      .map(tag => ({ id: tag, label: toTitle(tag) }));
    return [...BASE_FILTERS, ...extras];
  }, [availableTags]);

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (debouncedQuery) params.append('query', debouncedQuery);
      if (focusFilter && focusFilter !== 'all') params.append('focus', focusFilter);
      const queryString = params.toString();

      const response = await apiFetch(`/api/planner-sessions${queryString ? `?${queryString}` : ''}`);
      if (!response.ok) {
        throw new Error('Unable to load sessions');
      }
      const data = await response.json();
      setSessions(data.sessions || []);
      setAvailableTags(data.available_focus_tags || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load planner sessions', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, focusFilter]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleSelect = (session) => {
    if (onSessionSelect) {
      onSessionSelect(session);
    }
  };

  const getFilterLabel = (filterId) => {
    const found = derivedFilters.find(f => f.id === filterId);
    return found?.label || toTitle(filterId);
  };

  return (
    <div className="sp-card">
      <div className="sp-card__header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <span className="sp-label">Session History</span>
            <h2 className="sp-card__title" style={{ marginTop: '0.25rem', textTransform: 'none', letterSpacing: 'normal' }}>
              Past lessons to inform your next one
            </h2>
          </div>
          <div style={{ minWidth: '260px', flex: '1 1 260px', maxWidth: '400px' }}>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search topics, challenges, or next steps"
              className="sp-input"
            />
          </div>
        </div>
      </div>

      {/* Focus filters */}
      <div className="sp-filters">
        {derivedFilters.map(filter => (
          <button
            key={filter.id}
            onClick={() => setFocusFilter(filter.id)}
            className={`sp-filter-btn ${focusFilter === filter.id ? 'sp-filter-btn--active' : ''}`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="sp-card__body">
        {loading && (
          <div className="sp-loading">
            <div className="lrbrain-typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p className="sp-loading__text">Loading session history...</p>
          </div>
        )}

        {!loading && error && (
          <div style={{
            padding: '1rem',
            borderRadius: '4px',
            border: '1px solid var(--lrbrain-error)',
            background: 'rgba(239,68,68,0.05)'
          }}>
            <p className="sp-text" style={{ color: 'var(--lrbrain-error)', margin: 0 }}>
              Unable to load sessions: {error}
            </p>
            <button onClick={loadSessions} className="sp-btn sp-btn--accent sp-btn--sm" style={{ marginTop: '0.75rem' }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && sessions.length === 0 && (
          <div className="sp-empty">
            <p className="sp-empty__text">No sessions match "{debouncedQuery || getFilterLabel(focusFilter)}".</p>
          </div>
        )}

        {!loading && !error && sessions.length > 0 && (
          <div className="sp-history__grid">
            {sessions.map(session => {
              const isSelected = selectedSessionId === session.id;
              return (
                <div
                  key={session.id}
                  className={`sp-session-card ${isSelected ? 'sp-session-card--selected' : ''}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span className="sp-session-card__date">{session.display_date || 'Recent session'}</span>
                    <span className="sp-session-card__date">{session.subject}</span>
                  </div>

                  <h3 className="sp-session-card__headline">{session.headline}</h3>

                  <p className="sp-session-card__overview">
                    {session.overview || 'No overview recorded for this session.'}
                  </p>

                  <div className="sp-tags" style={{ marginBottom: '0.75rem' }}>
                    {session.focus_tags?.map(tag => (
                      <span key={tag} className="sp-tag">{toTitle(tag)}</span>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div>
                      <span className="sp-label">Challenges</span>
                      <ul className="sp-list" style={{ marginTop: '0.375rem' }}>
                        {(session.challenges || []).slice(0, 2).map((challenge, idx) => (
                          <li key={idx} className="sp-list-item sp-list-item--warning" style={{ fontSize: '0.8125rem' }}>{challenge}</li>
                        ))}
                        {session.challenges?.length === 0 && (
                          <li className="sp-list-item" style={{ fontSize: '0.8125rem', color: 'var(--lrbrain-text-light)' }}>No challenges logged.</li>
                        )}
                      </ul>
                    </div>
                    <div>
                      <span className="sp-label">Next Focus</span>
                      <ul className="sp-list" style={{ marginTop: '0.375rem' }}>
                        {(session.next_steps || []).slice(0, 2).map((step, idx) => (
                          <li key={idx} className="sp-list-item sp-list-item--success" style={{ fontSize: '0.8125rem' }}>{step}</li>
                        ))}
                        {session.next_steps?.length === 0 && (
                          <li className="sp-list-item" style={{ fontSize: '0.8125rem', color: 'var(--lrbrain-text-light)' }}>Not specified.</li>
                        )}
                      </ul>
                    </div>
                  </div>

                  <button
                    onClick={() => handleSelect(session)}
                    className={`sp-btn sp-btn--full ${isSelected ? 'sp-btn--accent' : 'sp-btn--primary'}`}
                    style={{ marginTop: 'auto' }}
                  >
                    {isSelected ? 'Selected for planning' : 'Use these insights'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionHistoryPanel;
